import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { telechargerModeleReprisePrets, useActionsPortefeuille, usePortefeuille, useStructures, type RapportReprisePrets } from '@/api/hooks';
import { LIBELLE_STATUT_PRET_ACCORDE, type StatutPretAccorde } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { montantDevise, StatutPretAccordeBadge } from '@/components/rh';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT_PRET_ACCORDE) as StatutPretAccorde[];
const usd = (n: number) => montantDevise(n, 'USD');

/** Portefeuille des prêts (§ 5.9 et 5.11) : indicateurs, rendement par banque, liste des prêts accordés, exports, reprise. */
export function Portefeuille() {
  const u = useSession()!.utilisateur;
  const [statut, setStatut] = useState<StatutPretAccorde | ''>('');
  const [structure, setStructure] = useState('');
  const [banque, setBanque] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = usePortefeuille({ statut: statut || undefined, structure: structure || undefined, banque: banque || undefined });
  const [reprise, setReprise] = useState(false);
  const peutReprendre = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'INVESTISSEMENTS', 'DIRECTEUR_FINANCIER'].includes(p));
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const i = data.indicateurs;
  return (
    <div>
      <PageHeader
        titre="Portefeuille des prêts sociaux"
        sousTitre="Demandes, montants, encours, intérêts, échéances non retenues, créances en recouvrement, rendement par banque ; liste des prêts accordés."
        actions={
          <div className="flex flex-wrap gap-2">
            <BoutonExcel chemin={`/rh/portefeuille.xlsx${statut ? `?statut=${statut}` : ''}`} nom="portefeuille-prets.xlsx" libelle="Exporter (Excel)" taille="default" />
            {peutReprendre && (
              <Button variant="outline" onClick={() => setReprise(true)}>
                Reprise des prêts en cours
              </Button>
            )}
          </div>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="indicateurs-prets">
        <Kpi label="Prêts en force" valeur={i.pretsEnForce} detail={`${i.aDecaisser} à décaisser`} />
        <Kpi label="Capital restant dû" valeur={usd(i.capitalRestantDu)} detail={`décaissé ${usd(i.montantsDecaisses.total)} (net ${usd(i.montantsDecaisses.net)}, rachats ${usd(i.montantsDecaisses.rachat)})`} />
        <Kpi label="Intérêts encaissés" valeur={usd(i.interetsEncaisses)} detail={`échus ${usd(i.interetsEchus)} · prévisionnels ${usd(i.interetsPrevisionnels)}`} />
        <Kpi label="Échéances non retenues" valeur={i.echeancesNonRetenues} detail={`${i.creancesEnRecouvrement.nombre} créance(s) en recouvrement · ${usd(i.creancesEnRecouvrement.montant)}`} accent={i.echeancesNonRetenues > 0 || i.creancesEnRecouvrement.nombre > 0 ? 'alerte' : 'ok'} />
        <Kpi label="Demandes" valeur={Object.values(i.demandes).reduce((t, n) => t + n, 0)} detail={Object.entries(i.demandes).map(([s, n]) => `${n} ${s.toLowerCase().replace(/_/g, ' ')}`).join(' · ') || 'aucune'} />
        <Kpi label="Montants demandés / approuvés" valeur={usd(i.montantsApprouves)} detail={`demandés ${usd(i.montantsDemandes)}`} />
        <Kpi label="Délai moyen de décision" valeur={i.delaiMoyenJours === null ? '—' : `${i.delaiMoyenJours} j`} detail="de la réception par la DF à la décision finale" />
        <Kpi label="Taux d'approbation" valeur={i.tauxApprobation === null ? '—' : `${i.tauxApprobation} %`} detail={i.tauxRejet === null ? '' : `rejet ${i.tauxRejet} %`} />
      </div>
      {i.rendement.length > 0 && (
        <Card className="mb-4" data-testid="rendement">
          <CardHeader>
            <CardTitle>Rendement par banque ou compte de décaissement</CardTitle>
            <CardDescription>Les intérêts à collecter sont prévisionnels ; seuls les intérêts encaissés sont constatés.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banque / compte</TableHead>
                  <TableHead className="text-right">Prêts en force</TableHead>
                  <TableHead className="text-right">Encours</TableHead>
                  <TableHead className="text-right">Intérêts à collecter</TableHead>
                  <TableHead className="text-right">Intérêts encaissés</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {i.rendement.map((r) => (
                  <TableRow key={r.banque}>
                    <TableCell>{r.banque}</TableCell>
                    <TableCell className="num text-right">{r.pretsEnForce}</TableCell>
                    <TableCell className="num text-right">{usd(r.encours)}</TableCell>
                    <TableCell className="num text-right">{usd(r.interetsACollecter)}</TableCell>
                    <TableCell className="num text-right">{usd(r.interetsEncaisses)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SelectNatif value={statut} onChange={(e) => setStatut(e.target.value as StatutPretAccorde | '')}>
          <option value="">Tous les statuts</option>
          {STATUTS.map((s) => (
            <option key={s} value={s}>
              {LIBELLE_STATUT_PRET_ACCORDE[s]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <Input className="w-56" placeholder="Banque ou compte…" value={banque} onChange={(e) => setBanque(e.target.value)} />
      </div>
      {!data.prets.length ? (
        <Vide message="Aucun prêt accordé." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prêt</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Banque</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Capital restant dû</TableHead>
                <TableHead className="hidden lg:table-cell">Prochaine échéance</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Non retenues</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.prets.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link to={p.demandeId ? `/rh/prets/${p.demandeId}?onglet=remboursement` : `/rh/portefeuille/${p.id}`} className="font-medium text-primary hover:underline">
                      {p.numero}
                    </Link>
                    <div className="text-xs text-muted-foreground">{p.dateDecaissement ? `décaissé le ${dateFr(p.dateDecaissement)}` : 'à décaisser'}</div>
                  </TableCell>
                  <TableCell>
                    {p.agent.nom}
                    <div className="text-xs text-muted-foreground">{p.structure.code}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{p.banque ?? '—'}</TableCell>
                  <TableCell className="num text-right">
                    {montantDevise(p.montantTotal, p.devise)}
                    <div className="text-xs text-muted-foreground">
                      {p.dureeMois} mois · {montantDevise(p.mensualite, p.devise)}
                    </div>
                  </TableCell>
                  <TableCell className="num text-right">{montantDevise(p.solde.capitalRestant, p.devise)}</TableCell>
                  <TableCell className="hidden lg:table-cell">{p.solde.prochaineEcheance ? `${dateFr(p.solde.prochaineEcheance.date)} · ${montantDevise(p.solde.prochaineEcheance.mensualite, p.devise)}` : '—'}</TableCell>
                  <TableCell className="num text-right hidden sm:table-cell">{p.solde.nonRetenues}</TableCell>
                  <TableCell>
                    <StatutPretAccordeBadge statut={p.statut} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RepriseDialog ouvert={reprise} onClose={() => setReprise(false)} />
    </div>
  );
}

type ModeReprise = 'situation' | 'historique';
const LIGNE_VIDE = { matricule: '', numero: '', nature: 'CONSOMMATION', devise: 'USD', montantTotal: '', montantRachat: '', tauxAnnuel: '8', dureeMois: '', mensualite: '', banque: '', referencePaiement: '', dateDecaissement: '', mode: 'situation' as ModeReprise, capitalRestantDu: '', echeancesRestantes: '', prochaineEcheance: '', interetsPayes: '', observations: '', remboursements: '' };
type Saisie = typeof LIGNE_VIDE;
const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v.replace(/\s/g, '').replace(',', '.')));

/**
 * Reprise des prêts en cours (§ 10.4 ; décision du DG du 4 novembre 2026) : situation arrêtée à une date (31 décembre 2026 par
 * défaut) saisie par la Division des investissements, les RH ou le DF, prêt par prêt ou par classeur Excel établi sur le modèle ;
 * les remboursements constatés un à un restent possibles. Contrôle tout ou rien, simulation avant écriture.
 */
function RepriseDialog({ ouvert, onClose }: { ouvert: boolean; onClose: () => void }) {
  const actions = useActionsPortefeuille();
  const [dateSituation, setDateSituation] = useState('2026-12-31');
  const [fichier, setFichier] = useState<File | null>(null);
  const [saisie, setSaisie] = useState<Saisie>({ ...LIGNE_VIDE });
  const [lignes, setLignes] = useState<Array<Record<string, unknown>>>([]);
  const [rapport, setRapport] = useState<RapportReprisePrets | null>(null);
  const champ = <K extends keyof Saisie>(k: K, v: Saisie[K]) => setSaisie((x) => ({ ...x, [k]: v }));
  const message = (e: unknown) => (e instanceof Error ? e.message : 'Reprise impossible.');
  const ajouter = () => {
    if (!saisie.matricule.trim() || !num(saisie.montantTotal) || !num(saisie.dureeMois) || !saisie.dateDecaissement) {
      toast.error('Matricule, montant total, durée et date de décaissement sont obligatoires.');
      return;
    }
    if (saisie.mode === 'situation' && num(saisie.capitalRestantDu) === undefined) {
      toast.error('Indiquez le capital restant dû à la date de situation (0 si le prêt est soldé).');
      return;
    }
    const commun = { matricule: saisie.matricule.trim(), numero: saisie.numero.trim() || undefined, nature: saisie.nature, devise: saisie.devise, montantTotal: num(saisie.montantTotal), montantRachat: num(saisie.montantRachat), tauxAnnuel: num(saisie.tauxAnnuel) ?? 8, dureeMois: num(saisie.dureeMois), mensualite: num(saisie.mensualite), banque: saisie.banque.trim() || undefined, referencePaiement: saisie.referencePaiement.trim() || undefined, dateDecaissement: saisie.dateDecaissement };
    const ligne =
      saisie.mode === 'situation'
        ? { ...commun, capitalRestantDu: num(saisie.capitalRestantDu), echeancesRestantes: num(saisie.echeancesRestantes), prochaineEcheance: saisie.prochaineEcheance || undefined, interetsPayes: num(saisie.interetsPayes), observations: saisie.observations.trim() || undefined }
        : {
            ...commun,
            remboursements: saisie.remboursements
              .split(/[,;\n]/)
              .map((r) => r.trim())
              .filter(Boolean)
              .map((r) => {
                const [date, montant] = r.split('=');
                return { date: date?.trim() ?? '', montant: Number((montant ?? '').replace(',', '.')) };
              }),
          };
    setLignes((l) => [...l, ligne]);
    setSaisie({ ...LIGNE_VIDE, mode: saisie.mode });
    setRapport(null);
  };
  const lancer = async (simulation: boolean) => {
    if (!lignes.length) return;
    try {
      const r = await actions.reprisePrets.mutateAsync({ lignes, simulation, dateSituation: dateSituation || undefined });
      setRapport(r);
      if (!r.erreurs.length && !simulation) {
        toast.success(`${r.ecrites} prêt(s) repris.`);
        setLignes([]);
      }
    } catch (e) {
      toast.error(message(e));
    }
  };
  const importer = async (simulation: boolean) => {
    if (!fichier) return;
    try {
      const r = await actions.importReprisePrets.mutateAsync({ fichier, simulation, dateSituation: dateSituation || undefined });
      setRapport(r);
      if (!r.erreurs.length && !simulation) toast.success(`${r.ecrites} prêt(s) repris depuis le classeur.`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const modele = async () => {
    try {
      await telechargerModeleReprisePrets();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Reprise des prêts en cours</DialogTitle>
          <DialogDescription>
            Situation arrêtée à la date ci-dessous (31 décembre 2026 par défaut) : pour chaque prêt, les conditions accordées et le décaissement, puis le capital restant dû, les échéances restantes et, si connus, les intérêts déjà payés et la prochaine échéance ; l'échéancier repart de cette date. Les remboursements constatés un à un restent possibles. Contrôle tout ou rien : simulez d'abord, puis écrivez.
          </DialogDescription>
        </DialogHeader>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Par classeur Excel</CardTitle>
            <CardDescription>Téléchargez le modèle, remplissez la feuille « Prêts en cours » (une ligne par prêt), puis importez-le en simulation avant d'écrire.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="rep-date">Date de situation</Label>
              <Input id="rep-date" type="date" value={dateSituation} onChange={(e) => setDateSituation(e.target.value)} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="rep-fichier">Classeur (.xlsx)</Label>
              <Input id="rep-fichier" type="file" accept=".xlsx" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={modele}>
                Modèle Excel
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!fichier || actions.importReprisePrets.isPending} onClick={() => importer(true)}>
                Contrôler
              </Button>
              <Button type="button" size="sm" disabled={!fichier || actions.importReprisePrets.isPending} onClick={() => importer(false)}>
                Écrire
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prêt par prêt</CardTitle>
            <CardDescription>Renseignez un prêt, ajoutez-le à la liste, puis contrôlez et écrivez la liste.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="rep-matricule">Matricule *</Label>
                <Input id="rep-matricule" value={saisie.matricule} onChange={(e) => champ('matricule', e.target.value)} placeholder="F-0021" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-numero">Numéro (PS-nnnn, facultatif)</Label>
                <Input id="rep-numero" value={saisie.numero} onChange={(e) => champ('numero', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-nature">Nature</Label>
                <SelectNatif id="rep-nature" value={saisie.nature} onChange={(e) => champ('nature', e.target.value)}>
                  <option value="CONSOMMATION">Prêt à la consommation</option>
                  <option value="INVESTISSEMENT">Prêt d'investissement</option>
                </SelectNatif>
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-devise">Devise</Label>
                <SelectNatif id="rep-devise" value={saisie.devise} onChange={(e) => champ('devise', e.target.value)}>
                  <option value="USD">USD</option>
                  <option value="CDF">CDF</option>
                </SelectNatif>
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-total">Montant total *</Label>
                <Input id="rep-total" inputMode="decimal" value={saisie.montantTotal} onChange={(e) => champ('montantTotal', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-rachat">Encours racheté</Label>
                <Input id="rep-rachat" inputMode="decimal" value={saisie.montantRachat} onChange={(e) => champ('montantRachat', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-taux">Taux annuel (%)</Label>
                <Input id="rep-taux" inputMode="decimal" value={saisie.tauxAnnuel} onChange={(e) => champ('tauxAnnuel', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-duree">Durée (mois) *</Label>
                <Input id="rep-duree" inputMode="numeric" value={saisie.dureeMois} onChange={(e) => champ('dureeMois', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-mensualite">Mensualité du classeur</Label>
                <Input id="rep-mensualite" inputMode="decimal" value={saisie.mensualite} onChange={(e) => champ('mensualite', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-banque">Banque ou compte</Label>
                <Input id="rep-banque" value={saisie.banque} onChange={(e) => champ('banque', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-ref">Référence du paiement</Label>
                <Input id="rep-ref" value={saisie.referencePaiement} onChange={(e) => champ('referencePaiement', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-decaissement">Date de décaissement *</Label>
                <Input id="rep-decaissement" type="date" value={saisie.dateDecaissement} onChange={(e) => champ('dateDecaissement', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rep-mode">Mode</Label>
                <SelectNatif id="rep-mode" value={saisie.mode} onChange={(e) => champ('mode', e.target.value as ModeReprise)}>
                  <option value="situation">Situation arrêtée à la date</option>
                  <option value="historique">Remboursements constatés un à un</option>
                </SelectNatif>
              </div>
              {saisie.mode === 'situation' ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="rep-capital">Capital restant dû au {dateFr(dateSituation)} *</Label>
                    <Input id="rep-capital" inputMode="decimal" value={saisie.capitalRestantDu} onChange={(e) => champ('capitalRestantDu', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rep-restantes">Échéances restantes</Label>
                    <Input id="rep-restantes" inputMode="numeric" value={saisie.echeancesRestantes} onChange={(e) => champ('echeancesRestantes', e.target.value)} placeholder="déduites sinon" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rep-prochaine">Prochaine échéance</Label>
                    <Input id="rep-prochaine" type="date" value={saisie.prochaineEcheance} onChange={(e) => champ('prochaineEcheance', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rep-interets">Intérêts déjà payés</Label>
                    <Input id="rep-interets" inputMode="decimal" value={saisie.interetsPayes} onChange={(e) => champ('interetsPayes', e.target.value)} />
                  </div>
                  <div className="space-y-1 sm:col-span-3 lg:col-span-4">
                    <Label htmlFor="rep-obs">Observations</Label>
                    <Input id="rep-obs" value={saisie.observations} onChange={(e) => champ('observations', e.target.value)} />
                  </div>
                </>
              ) : (
                <div className="space-y-1 sm:col-span-3 lg:col-span-4">
                  <Label htmlFor="rep-remb">Remboursements constatés (AAAA-MM-JJ=montant, séparés par des virgules)</Label>
                  <Textarea id="rep-remb" rows={2} className="font-mono text-xs" value={saisie.remboursements} onChange={(e) => champ('remboursements', e.target.value)} placeholder="2026-08-31=108.55, 2026-09-30=108.55" />
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={ajouter}>
                Ajouter à la liste
              </Button>
            </div>
            {lignes.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matricule</TableHead>
                      <TableHead>Prêt</TableHead>
                      <TableHead>Décaissé le</TableHead>
                      <TableHead>Situation ou remboursements</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lignes.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{String(l.matricule)}</TableCell>
                        <TableCell className="text-sm">
                          {String(l.numero ?? '')} {String(l.nature)} · {String(l.montantTotal)} {String(l.devise)} · {String(l.tauxAnnuel)} % · {String(l.dureeMois)} mois
                        </TableCell>
                        <TableCell className="text-sm">{dateFr(String(l.dateDecaissement))}</TableCell>
                        <TableCell className="text-sm">{'capitalRestantDu' in l ? `capital restant dû ${String(l.capitalRestantDu)}${l.echeancesRestantes !== undefined ? ` · ${String(l.echeancesRestantes)} échéance(s) restante(s)` : ''}` : `${(l.remboursements as unknown[]).length} remboursement(s) constaté(s)`}</TableCell>
                        <TableCell className="text-right">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setLignes((x) => x.filter((_, k) => k !== i))}>
                            Retirer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" disabled={actions.reprisePrets.isPending} onClick={() => lancer(true)}>
                    Contrôler la liste
                  </Button>
                  <Button type="button" disabled={actions.reprisePrets.isPending} onClick={() => lancer(false)}>
                    Écrire la liste
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        {rapport && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{rapport.erreurs.length ? `${rapport.erreurs.length} erreur(s) : rien n'est écrit` : rapport.ecrites ? `${rapport.ecrites} prêt(s) repris` : `${rapport.lignes} ligne(s) valide(s), rien d'écrit (simulation)`}</CardTitle>
              {rapport.ecarts.length > 0 && <CardDescription>{rapport.ecarts.length} écart(s) avec le classeur (mensualité conservée telle qu'accordée).</CardDescription>}
            </CardHeader>
            {(rapport.erreurs.length > 0 || rapport.ecarts.length > 0) && (
              <CardContent className="text-sm">
                {rapport.erreurs.map((e, i) => (
                  <div key={`e${i}`} className="text-destructive">
                    Ligne {e.ligne} : {e.message}
                  </div>
                ))}
                {rapport.ecarts.map((e, i) => (
                  <div key={`x${i}`} className="text-amber-700">
                    Ligne {e.ligne} ({e.numero}) : {e.message}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
